import { useCallback, useEffect, useState } from 'react'

import api from '@disco/api/server'
import captureError from '@disco/utils/captureError'

/**
 * Loads the Chargebee pricing page url for the business's subscription, for CTAs that link to it.
 * @param redirect - DISCO path Chargebee sends the user back to after checkout
 * @param enabled - Skips the request when nobody on the page can use the link
 * @returns the url once loaded, whether it failed to load, and `refresh` to request a new session
 */
export default function useChargebeePricingPage(
  redirect: string,
  enabled = true,
): { url?: string; hasFailed: boolean; refresh: () => void } {
  const [url, setUrl] = useState<string>()
  const [hasFailed, setHasFailed] = useState(false)
  const [refreshCount, setRefreshCount] = useState(0)

  useEffect(() => {
    if (!enabled) return

    let isCurrent = true
    api
      .chargebeeSubscriberPricingForm({ redirect })
      .then(response => {
        if (!response?.url) {
          throw new Error('Chargebee pricing page returned no url')
        }
        if (!isCurrent) return
        setUrl(response.url)
        setHasFailed(false)
      })
      .catch(error => {
        if (!isCurrent) return
        // Also on a refresh: the session already loaded may have been spent in Chargebee
        setHasFailed(true)
        captureError(error, {
          moduleName: 'useChargebeePricingPage',
          description: 'Failed to load the Chargebee pricing page url',
        })
      })

    return () => {
      isCurrent = false
    }
  }, [redirect, enabled, refreshCount])

  const refresh = useCallback(() => setRefreshCount(count => count + 1), [])

  return { url, hasFailed, refresh }
}
